import { z } from 'zod';
import { costCategorySchema, costStatusSchema, workOptionSchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { amountSchema, isoDateSchema } from '../../common/schemas/money.schemas.js';
import { ownerSchema } from '../../common/schemas/owner.schemas.js';
/**
 * Cost items — the one authoritative cost pipeline (plan §5.4, §5.5; PP §7.2).
 *
 * They belong to **either owner**: on an estimate they are the *expected work* a salesperson records
 * while the customer is still in front of them, and the purchase copies them onto the stock vehicle as
 * `ESTIMATED` rows with `copied_from_id`. From then on the workshop books actuals against the same rows.
 *
 * Two product rules are encoded in the shapes here, not merely in the service:
 *
 *  - **An item contributes one figure, never two.** `estimate`, `actual_to_date` and `remaining_estimate`
 *    all exist on the same row, and the status decides which of them is read (plan §5.5). "Do not sum the
 *    full original estimate with actuals" (PP §7.2).
 *  - **Holding is not a cost item.** `costCategorySchema` has no `HOLDING` member: holding is produced by
 *    the `holding` equation alone and is therefore accounted exactly once (plan §3.3).
 */
/** The five prototype work buttons (PP §7.3). `MANUAL` is a quoted price with a reason. */
export const costItemSchema = z.object({
    id: uuidSchema,
    owner: ownerSchema,
    category: costCategorySchema,
    workOption: workOptionSchema.nullable(),
    name: z.string(),
    estimate: amountSchema.nullable(),
    actualToDate: amountSchema.nullable(),
    remainingEstimate: amountSchema.nullable(),
    status: costStatusSchema,
    supplier: z.string().nullable(),
    workDate: isoDateSchema.nullable(),
    /** The INVOICE document that proves the actual, when one is attached (plan §8; PP §8). */
    invoiceDocumentId: uuidSchema.nullable(),
    notes: z.string().nullable(),
    /** The estimate row this was copied from at the purchase, when it was. */
    copiedFromId: uuidSchema.nullable(),
    /** What the resolution pipeline reads from this row today (plan §5.5). */
    resolvedAmount: z.number(),
    resolvedBasis: z.enum(['ESTIMATE', 'ACTUAL', 'ACTUAL_PLUS_REMAINING', 'WAIVED']),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** The summary an estimate or a vehicle detail carries, so one round trip answers "what is booked?". */
export const costItemsSummarySchema = z.object({
    count: z.number().int().min(0),
    /** Σ of what each item resolves to today. */
    resolvedTotal: z.number(),
    /** True while any item still contributes an estimate rather than an actual. */
    provisional: z.boolean(),
});
export const listCostItemsInputSchema = z.object({
    owner: ownerSchema,
});
export const listCostItemsOutputSchema = z.object({
    owner: ownerSchema,
    items: z.array(costItemSchema),
    summary: costItemsSummarySchema,
});
/**
 * The prototype's five buttons. The four body/battery options are priced as a **rate × the current
 * appraisal**, the oil service as the template's flat fee, and each of the five may exist only once —
 * "a checkbox creates an estimate item once" (PP §7.3). `MANUAL` is `addManual` instead.
 */
export const addWorkOptionInputSchema = z.object({
    owner: ownerSchema,
    workOption: workOptionSchema.exclude(['MANUAL']),
    note: z.string().trim().max(2_000).optional(),
});
export const addManualCostInputSchema = z.object({
    owner: ownerSchema,
    category: costCategorySchema,
    name: z.string().trim().min(1, 'Name the work or the fee').max(200),
    estimate: amountSchema,
    /** A manual amount overrides a shared value, so it says why (PP §7.3). */
    reason: z.string().trim().min(1, 'Say why this amount was quoted').max(2_000),
});
/**
 * PATCH semantics. The status transitions are the service's (plan §5.5): `IN_PROGRESS` needs a remaining
 * estimate, `DONE` needs an actual, and `WAIVED` is only ever reached through `costs.waive`.
 */
export const updateCostItemInputSchema = z.object({
    id: uuidSchema,
    /** The owner's revision, so a cost edit cannot land on a record a second tab has moved on. */
    expectedRevision: z.number().int().min(1).optional(),
    estimate: amountSchema.nullish(),
    actualToDate: amountSchema.nullish(),
    remainingEstimate: amountSchema.nullish(),
    status: costStatusSchema.exclude(['WAIVED']).optional(),
    supplier: z.string().trim().max(200).nullish(),
    workDate: isoDateSchema.nullish(),
    /** An INVOICE document **on the same vehicle**; the service refuses anything else (plan §8). */
    invoiceDocumentId: uuidSchema.nullish(),
    notes: z.string().trim().max(2_000).nullish(),
});
export const removeCostItemInputSchema = z.object({
    id: uuidSchema,
});
export const waiveCostItemInputSchema = z.object({
    id: uuidSchema,
    reason: z.string().trim().min(1, 'Say why the work is not needed').max(2_000),
});
//# sourceMappingURL=costs.schemas.js.map